export const CLOSE_PUSH_BLOCKED_REASONS = [
  "not_deceased",
  "blocked_no_owner",
  "blocked_no_value",
  "blocked_no_plv",
  "blocked_no_equity",
  "blocked_equity_below_25k",
  "blocked_no_obituary"
] as const;

export type ClosePushBlockedReason = (typeof CLOSE_PUSH_BLOCKED_REASONS)[number];

export type CloseCreateFailureClass =
  | "close_not_deceased"
  | "close_equity_not_finite"
  | "close_equity_below_threshold"
  | "close_obituary_required";

/** Locked Close create floor: deceased + estimated equity ≥ $25,000. */
export const CLOSE_EQUITY_MIN_USD = 25_000;

export type CloseCreateDecision =
  | { action: "create" }
  | { action: "patch" }
  | { action: "refuse"; failureClass: CloseCreateFailureClass; reason: ClosePushBlockedReason };

export type PropertyValueSource = "zestimate" | "attom_avm";

export const EQUITY_FORMULA_VERSION = "equity-formula-v1";
export const EQUITY_FORMULA_INPUT_VERSION = "equity-formula-input-v1";
export const EQUITY_FORMULA_OUTPUT_VERSION = EQUITY_FORMULA_VERSION;
export const EQUITY_FORMULA_POLICY_VERSION = EQUITY_FORMULA_VERSION;
/** Current auction amount − affidavit principal. Supersedes SO-5 EPLV × 115%. */
export const AUCTION_EQUITY_FORMULA_VERSION = "auction-equity-v2";
/** SO-5: auctionPrice − (PLV × 115%). Never project as a current-formula value. */
export const AUCTION_EQUITY_LEGACY_FORMULA_VERSION = "auction-equity-v1";
export const DEFAULT_MARKET_VALUE_PCT = 80;
export const DEFAULT_LOAN_VALUE_PCT = 115;
export const DEFAULT_EQUITY_MARKET_VALUE_PCT = DEFAULT_MARKET_VALUE_PCT;
export const DEFAULT_EQUITY_LOAN_VALUE_PCT = DEFAULT_LOAN_VALUE_PCT;
/** Unspecified selector keeps the historical ATTOM-first fallback. Workflow policy overrides this. */
export const DEFAULT_VALUE_PREFERENCE: readonly PropertyValueSource[] = ["attom_avm", "zestimate"];

export type EquityPolicy = {
  formulaVersion: string;
  marketValuePct: number;
  loanValuePct: number;
  preference: PropertyValueSource[];
};

export type EquityProvenance = {
  policyVersion: string;
  valueSource: PropertyValueSource | null;
  value: number | null;
  plv: number | null;
  valueRunId?: string | null;
  plvRunId?: string | null;
};

export type CalculatedEquity = {
  equity: number | null;
  value: number | null;
  source: PropertyValueSource | null;
  plv: number | null;
  policyVersion: string;
  formulaVersion: string;
  marketValuePct: number;
  loanValuePct: number;
  valueRunId?: string | null;
  plvRunId?: string | null;
};

export const AUCTION_EQUITY_AMOUNT_KINDS = ["upset_bid", "reported_sale_amount"] as const;
export type AuctionEquityAmountKind = (typeof AUCTION_EQUITY_AMOUNT_KINDS)[number];

export const AUCTION_EQUITY_FACT_STATUSES = [
  "valid",
  "ambiguous",
  "superseded",
  "unreadable",
  "missing"
] as const;
export type AuctionEquityFactStatus = (typeof AUCTION_EQUITY_FACT_STATUSES)[number];

export const AUCTION_EQUITY_UNAVAILABLE_REASONS = [
  "missing_current_amount",
  "missing_affidavit_principal",
  "ambiguous_current_amount",
  "ambiguous_affidavit_principal",
  "superseded_current_amount",
  "superseded_affidavit_principal",
  "unreadable_newer_upset_amount",
  "legacy_formula_value"
] as const;
export type AuctionEquityUnavailableReason = (typeof AUCTION_EQUITY_UNAVAILABLE_REASONS)[number];

export const AUCTION_EQUITY_INVALIDATION_STATES = [
  "current",
  "transient_retrieval_failure",
  "authoritative_invalidation"
] as const;
export type AuctionEquityInvalidationState = (typeof AUCTION_EQUITY_INVALIDATION_STATES)[number];

export const AUCTION_EQUITY_RETRIEVALS = ["ok", "transient_failure", "authoritative_invalidation"] as const;
export type AuctionEquityRetrieval = (typeof AUCTION_EQUITY_RETRIEVALS)[number];

export type AuctionAmountFact = {
  kind: AuctionEquityAmountKind;
  /** Numeric dollars when readable. Null means a known event whose amount is unreadable. */
  amount: number | string | null;
  effectiveAt: string;
  evidenceRef: string;
  status: AuctionEquityFactStatus;
};

export type AffidavitPrincipalFact = {
  principal: number | string | null;
  asOf: string;
  evidenceRef: string;
  status: AuctionEquityFactStatus;
};

export type AuctionEquityFacts = {
  amounts?: readonly AuctionAmountFact[] | null;
  affidavitPrincipal?: AffidavitPrincipalFact | null;
};

export type AuctionEquityProvenance = {
  formulaVersion: string;
  currentAmount: number | null;
  currentAmountKind: AuctionEquityAmountKind | null;
  currentAmountEffectiveAt: string | null;
  currentAmountEvidenceRef: string | null;
  affidavitPrincipal: number | null;
  principalAsOf: string | null;
  principalEvidenceRef: string | null;
  invalidationState: AuctionEquityInvalidationState;
  unavailableReason: AuctionEquityUnavailableReason | null;
  computedAt: string | null;
  lastKnownEquity: number | null;
  lastKnownAt: string | null;
};

export type AuctionEquityCalculation = {
  available: boolean;
  /** Numeric dollars at API/Close boundaries. Zero and negative are valid. */
  equity: number | null;
  formulaVersion: string;
  currentAmount: number | null;
  currentAmountKind: AuctionEquityAmountKind | null;
  currentAmountEffectiveAt: string | null;
  currentAmountEvidenceRef: string | null;
  affidavitPrincipal: number | null;
  principalAsOf: string | null;
  principalEvidenceRef: string | null;
  invalidationState: AuctionEquityInvalidationState;
  unavailableReason: AuctionEquityUnavailableReason | null;
  computedAt: string | null;
  lastKnownEquity: number | null;
  lastKnownAt: string | null;
};

/** Projection Close delivery (SO-61) consumes. Never a v1/EPLV number. */
export type AuctionEquityCloseProjection = {
  formulaVersion: typeof AUCTION_EQUITY_FORMULA_VERSION;
  auctionEquity: number | null;
  unavailableReason: AuctionEquityUnavailableReason | null;
  invalidationState: AuctionEquityInvalidationState;
  currentAmount: number | null;
  currentAmountKind: AuctionEquityAmountKind | null;
  currentAmountEffectiveAt: string | null;
  currentAmountEvidenceRef: string | null;
  affidavitPrincipal: number | null;
  principalAsOf: string | null;
  principalEvidenceRef: string | null;
  lastKnownEquity: number | null;
  lastKnownAt: string | null;
  computedAt: string | null;
};

export type AuctionEquityPropertyPatch = {
  auction_equity: number | null;
  auction_equity_formula_version: string;
  auction_equity_invalidation_state: AuctionEquityInvalidationState;
  auction_equity_computed_at: string | null;
  auction_equity_last_known: number | null;
  auction_equity_last_known_at: string | null;
  sourceFieldsPatch: { auctionEquity: AuctionEquityProvenance };
};

export function finiteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const raw = value.replace(/[$,]/g, "").replaceAll(/\s+/g, " ").trim();
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/** True only for an explicit deceased yes. Missing / unknown / living is not deceased. */
export function isDeceased(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const raw = value.replaceAll(/\s+/g, " ").trim().toLowerCase();
    return raw === "yes" || raw === "y" || raw === "true" || raw === "1" || raw === "deceased";
  }
  return false;
}

export function propertyValueSourceFromTool(toolId: unknown): PropertyValueSource | null {
  if (toolId === "zillow_zestimate" || toolId === "zestimate") return "zestimate";
  if (toolId === "attom_avm") return "attom_avm";
  return null;
}

export function valuePreferenceFromTools(tools: readonly string[] | undefined): PropertyValueSource[] {
  const out: PropertyValueSource[] = [];
  for (const tool of tools ?? []) {
    const source = propertyValueSourceFromTool(tool);
    if (source && !out.includes(source)) out.push(source);
  }
  return out.length ? out : [...DEFAULT_VALUE_PREFERENCE];
}

export function equityPolicyFromSteps(
  steps: readonly {
    id?: string | undefined;
    tools: readonly string[];
    gate?: { marketValuePct?: number | undefined; loanValuePct?: number | undefined } | undefined;
  }[]
): EquityPolicy {
  const valueStep = steps.find((step) => step.tools.some((tool) => propertyValueSourceFromTool(tool) != null));
  const equityStep = steps.find((step) => step.tools.includes("equity_formula"));
  return {
    formulaVersion: EQUITY_FORMULA_VERSION,
    marketValuePct: finiteNumber(equityStep?.gate?.marketValuePct) ?? DEFAULT_MARKET_VALUE_PCT,
    loanValuePct: finiteNumber(equityStep?.gate?.loanValuePct) ?? DEFAULT_LOAN_VALUE_PCT,
    preference: valuePreferenceFromTools(valueStep?.tools)
  };
}

export function equityPolicyVersion(policy: EquityPolicy): string {
  return `${policy.formulaVersion}:${policy.preference.join(",")}:${policy.marketValuePct}:${policy.loanValuePct}`;
}

export function resolvePropertyValue(input: {
  zestimate?: unknown;
  attomAvm?: unknown;
  preference?: readonly string[];
}): { value: number | null; source: PropertyValueSource | null } {
  const bySource: Record<PropertyValueSource, number | null> = {
    attom_avm: finiteNumber(input.attomAvm),
    zestimate: finiteNumber(input.zestimate)
  };
  for (const source of valuePreferenceFromTools(input.preference)) {
    const value = bySource[source];
    if (value != null) return { value, source };
  }
  return { value: null, source: null };
}

/**
 * estimatedEquity = (value × marketValuePct%) − (plv × loanValuePct%).
 * Defaults are 80 / 115. plv == null is not 0.
 * Missing value or PLV leaves equity unavailable. Negative equity is finite
 * but does not pass the Close create floor.
 */
export function estimateEquity(input: {
  value?: unknown;
  plv?: unknown;
  marketValuePct?: number;
  loanValuePct?: number;
}): number | null {
  const value = finiteNumber(input.value);
  const plv = finiteNumber(input.plv);
  if (value == null || plv == null) return null;
  const marketPct = finiteNumber(input.marketValuePct) ?? DEFAULT_MARKET_VALUE_PCT;
  const loanPct = finiteNumber(input.loanValuePct) ?? DEFAULT_LOAN_VALUE_PCT;
  return roundMoney((value * marketPct) / 100 - (plv * loanPct) / 100);
}

export function calculatePropertyEquity(input: {
  zestimate?: unknown;
  attomAvm?: unknown;
  plv?: unknown;
  policy: EquityPolicy;
  valueRunId?: string | null;
  plvRunId?: string | null;
}): CalculatedEquity {
  const resolved = resolvePropertyValue({
    zestimate: input.zestimate,
    attomAvm: input.attomAvm,
    preference: input.policy.preference
  });
  const plv = finiteNumber(input.plv);
  return {
    equity: estimateEquity({
      value: resolved.value,
      plv,
      marketValuePct: input.policy.marketValuePct,
      loanValuePct: input.policy.loanValuePct
    }),
    value: resolved.value,
    source: resolved.source,
    plv,
    policyVersion: equityPolicyVersion(input.policy),
    formulaVersion: input.policy.formulaVersion,
    marketValuePct: input.policy.marketValuePct,
    loanValuePct: input.policy.loanValuePct,
    valueRunId: input.valueRunId ?? null,
    plvRunId: input.plvRunId ?? null
  };
}

export function equityProvenanceFromSourceFields(sourceFields: unknown): EquityProvenance | null {
  const rec = recordFromUnknown(sourceFields);
  const equity = recordFromUnknown(rec.equity);
  if (!Object.keys(equity).length) return null;
  const valueSource = equity.valueSource === "zestimate" || equity.valueSource === "attom_avm"
    ? equity.valueSource
    : null;
  return {
    policyVersion: String(equity.policyVersion ?? ""),
    valueSource,
    value: finiteNumber(equity.value),
    plv: finiteNumber(equity.plv),
    valueRunId: textOrNull(equity.valueRunId),
    plvRunId: textOrNull(equity.plvRunId)
  };
}

export function equityProvenancePatch(calculated: CalculatedEquity): Record<string, unknown> {
  return {
    formulaVersion: calculated.formulaVersion,
    policyVersion: calculated.policyVersion,
    marketValuePct: calculated.marketValuePct,
    loanValuePct: calculated.loanValuePct,
    valueSource: calculated.source,
    value: calculated.value,
    plv: calculated.plv,
    valueRunId: calculated.valueRunId ?? null,
    plvRunId: calculated.plvRunId ?? null
  };
}

export function shouldRecomputeEquity(input: {
  estimatedEquity?: unknown;
  stored?: EquityProvenance | null;
  calculated: CalculatedEquity;
}): boolean {
  if (input.calculated.value == null || input.calculated.plv == null) return false;
  if (input.calculated.equity == null) return false;
  const storedEquity = finiteNumber(input.estimatedEquity);
  if (storedEquity == null) return true;
  const stored = input.stored;
  if (!stored?.policyVersion) return true;
  if (stored.policyVersion !== input.calculated.policyVersion) return true;
  if (stored?.valueSource && stored.valueSource !== input.calculated.source) return true;
  if (stored?.value != null && stored.value !== input.calculated.value) return true;
  if (stored?.plv != null && stored.plv !== input.calculated.plv) return true;
  if ((stored?.valueRunId ?? null) !== (input.calculated.valueRunId ?? null) && input.calculated.valueRunId != null) return true;
  if ((stored?.plvRunId ?? null) !== (input.calculated.plvRunId ?? null) && input.calculated.plvRunId != null) return true;
  return storedEquity !== input.calculated.equity;
}

/**
 * auctionEquity = current auction amount − affidavit principal.
 * Current amount = latest valid upset bid, otherwise reported sale amount.
 * Missing/ambiguous/superseded inputs leave equity unavailable, not zero.
 */
export function estimateAuctionEquity(input: {
  currentAmount?: unknown;
  affidavitPrincipal?: unknown;
}): number | null {
  const amountCents = moneyToCents(input.currentAmount);
  const principalCents = moneyToCents(input.affidavitPrincipal);
  if (amountCents == null || principalCents == null) return null;
  return centsToDollars(amountCents - principalCents);
}

export function moneyToCents(value: unknown): number | null {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 100);
  }
  if (typeof value !== "string") return null;
  const raw = value.replace(/[$,]/g, "").replaceAll(/\s+/g, "").trim();
  if (!raw) return null;
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!match) return null;
  const negative = match[1] === "-";
  const whole = Number(match[2]);
  if (!Number.isSafeInteger(whole)) return null;
  const frac = match[3] ?? "";
  const digits = `${frac}000`.slice(0, 3);
  const cents = whole * 100 + Number(digits.slice(0, 2));
  const tenths = Number(digits[2] ?? "0");
  const rounded = tenths >= 5 ? cents + 1 : cents;
  return negative ? -rounded : rounded;
}

export function centsToDollars(cents: number): number {
  return Number((cents / 100).toFixed(2));
}

export function isAuctionEquityFormulaVersion(value: unknown): value is typeof AUCTION_EQUITY_FORMULA_VERSION {
  return value === AUCTION_EQUITY_FORMULA_VERSION;
}

export function auctionEquityFactsFromSourceFields(sourceFields: unknown): AuctionEquityFacts {
  const rec = recordFromUnknown(sourceFields);
  const nested = recordFromUnknown(rec.auctionEquityFacts ?? rec.auction_equity_facts);
  const amountsRaw = nested.amounts ?? rec.auctionEquityAmounts;
  const amounts = Array.isArray(amountsRaw)
    ? amountsRaw.map(parseAuctionAmountFact).filter((fact): fact is AuctionAmountFact => fact != null)
    : [];
  const principalRaw = nested.affidavitPrincipal ?? nested.affidavit_principal ?? rec.affidavitPrincipal;
  const affidavitPrincipal = parseAffidavitPrincipalFact(principalRaw);
  return {
    ...(amounts.length ? { amounts } : {}),
    ...(affidavitPrincipal ? { affidavitPrincipal } : {})
  };
}

export function auctionEquityProvenanceFromSourceFields(sourceFields: unknown): AuctionEquityProvenance | null {
  const rec = recordFromUnknown(sourceFields);
  return parseAuctionEquityProvenance(rec.auctionEquity ?? rec.auction_equity);
}

export function selectCurrentAuctionAmount(
  amounts: readonly AuctionAmountFact[] | null | undefined
): { ok: true; fact: AuctionAmountFact; amount: number } | { ok: false; reason: AuctionEquityUnavailableReason } {
  const parsed = (amounts ?? []).map(normalizeAuctionAmountFact).filter((fact): fact is NormalizedAuctionAmount => fact != null);
  if (!parsed.length) return { ok: false, reason: "missing_current_amount" };

  const upsets = parsed
    .filter((fact) => fact.kind === "upset_bid" && fact.status !== "superseded" && fact.status !== "missing")
    .sort(compareAmountRecency);
  const latestUpset = upsets[0] ?? null;
  if (latestUpset) {
    if (latestUpset.status === "ambiguous") return { ok: false, reason: "ambiguous_current_amount" };
    if (latestUpset.amountCents == null || latestUpset.status === "unreadable") {
      return { ok: false, reason: "unreadable_newer_upset_amount" };
    }
    if (latestUpset.status === "valid") {
      return {
        ok: true,
        fact: toAuctionAmountFact(latestUpset),
        amount: centsToDollars(latestUpset.amountCents)
      };
    }
    return { ok: false, reason: "missing_current_amount" };
  }

  const sales = parsed
    .filter((fact) => fact.kind === "reported_sale_amount" && fact.status !== "superseded")
    .sort(compareAmountRecency);
  const latestSale = sales[0] ?? null;
  if (!latestSale) return { ok: false, reason: "missing_current_amount" };
  if (latestSale.status === "ambiguous") return { ok: false, reason: "ambiguous_current_amount" };
  if (latestSale.status === "unreadable" || latestSale.amountCents == null) {
    return { ok: false, reason: "missing_current_amount" };
  }
  if (latestSale.status !== "valid") return { ok: false, reason: "missing_current_amount" };
  return {
    ok: true,
    fact: toAuctionAmountFact(latestSale),
    amount: centsToDollars(latestSale.amountCents)
  };
}

export function calculateAuctionEquity(input: {
  facts?: AuctionEquityFacts | null;
  sourceFields?: unknown;
  stored?: AuctionEquityProvenance | null;
  retrieval?: AuctionEquityRetrieval;
  auctionCompleted?: boolean;
  now?: string;
}): AuctionEquityCalculation {
  const now = textOrNull(input.now);
  const stored = input.stored ?? auctionEquityProvenanceFromSourceFields(input.sourceFields);
  const facts = mergeAuctionEquityFacts(input.facts, auctionEquityFactsFromSourceFields(input.sourceFields));
  const retrieval = input.retrieval ?? "ok";

  if (retrieval === "transient_failure") {
    return preserveLastKnown(stored, now, "transient_retrieval_failure");
  }
  if (retrieval === "authoritative_invalidation") {
    return clearCurrentValue(stored, now, stored?.unavailableReason ?? "superseded_current_amount");
  }

  const selected = selectCurrentAuctionAmount(facts.amounts);
  const principal = normalizeAffidavitPrincipal(facts.affidavitPrincipal);
  if (!selected.ok) {
    return unavailableCalculation({
      stored,
      now,
      reason: selected.reason,
      principal
    });
  }
  if (!principal.ok) {
    return unavailableCalculation({
      stored,
      now,
      reason: principal.reason,
      amount: selected
    });
  }

  const equity = centsToDollars(moneyToCents(selected.amount)! - principal.principalCents);
  const computedAt = now ?? stored?.computedAt ?? null;
  return {
    available: true,
    equity,
    formulaVersion: AUCTION_EQUITY_FORMULA_VERSION,
    currentAmount: selected.amount,
    currentAmountKind: selected.fact.kind,
    currentAmountEffectiveAt: textOrNull(selected.fact.effectiveAt),
    currentAmountEvidenceRef: textOrNull(selected.fact.evidenceRef),
    affidavitPrincipal: principal.principal,
    principalAsOf: principal.asOf,
    principalEvidenceRef: principal.evidenceRef,
    invalidationState: "current",
    unavailableReason: null,
    computedAt,
    lastKnownEquity: equity,
    lastKnownAt: computedAt
  };
}

export function auctionEquityForProperty(input: {
  facts?: AuctionEquityFacts | null;
  sourceFields?: unknown;
  stored?: AuctionEquityProvenance | null;
  retrieval?: AuctionEquityRetrieval;
  auctionCompleted?: boolean;
  now?: string;
}): number | null {
  return calculateAuctionEquity(input).equity;
}

export function auctionEquityProvenanceFromCalculation(
  calculated: AuctionEquityCalculation
): AuctionEquityProvenance {
  return {
    formulaVersion: calculated.formulaVersion,
    currentAmount: calculated.currentAmount,
    currentAmountKind: calculated.currentAmountKind,
    currentAmountEffectiveAt: calculated.currentAmountEffectiveAt,
    currentAmountEvidenceRef: calculated.currentAmountEvidenceRef,
    affidavitPrincipal: calculated.affidavitPrincipal,
    principalAsOf: calculated.principalAsOf,
    principalEvidenceRef: calculated.principalEvidenceRef,
    invalidationState: calculated.invalidationState,
    unavailableReason: calculated.unavailableReason,
    computedAt: calculated.computedAt,
    lastKnownEquity: calculated.lastKnownEquity,
    lastKnownAt: calculated.lastKnownAt
  };
}

export function auctionEquityPropertyPatch(
  calculated: AuctionEquityCalculation
): AuctionEquityPropertyPatch {
  const currentFormula = isAuctionEquityFormulaVersion(calculated.formulaVersion);
  const currentValue = currentFormula && calculated.invalidationState !== "authoritative_invalidation"
    ? calculated.equity
    : null;
  return {
    auction_equity: currentValue,
    auction_equity_formula_version: calculated.formulaVersion,
    auction_equity_invalidation_state: calculated.invalidationState,
    auction_equity_computed_at: calculated.computedAt,
    auction_equity_last_known: calculated.lastKnownEquity,
    auction_equity_last_known_at: calculated.lastKnownAt,
    sourceFieldsPatch: { auctionEquity: auctionEquityProvenanceFromCalculation(calculated) }
  };
}

export function projectAuctionEquityForClose(
  calculated: AuctionEquityCalculation
): AuctionEquityCloseProjection {
  const currentFormula = isAuctionEquityFormulaVersion(calculated.formulaVersion);
  const reason: AuctionEquityUnavailableReason | null = currentFormula
    ? calculated.unavailableReason
    : "legacy_formula_value";
  const equity = currentFormula && calculated.invalidationState !== "authoritative_invalidation"
    ? calculated.equity
    : null;
  return {
    formulaVersion: AUCTION_EQUITY_FORMULA_VERSION,
    auctionEquity: equity,
    unavailableReason: equity == null ? reason ?? (currentFormula ? calculated.unavailableReason : "legacy_formula_value") : null,
    invalidationState: currentFormula ? calculated.invalidationState : "authoritative_invalidation",
    currentAmount: currentFormula ? calculated.currentAmount : null,
    currentAmountKind: currentFormula ? calculated.currentAmountKind : null,
    currentAmountEffectiveAt: currentFormula ? calculated.currentAmountEffectiveAt : null,
    currentAmountEvidenceRef: currentFormula ? calculated.currentAmountEvidenceRef : null,
    affidavitPrincipal: currentFormula ? calculated.affidavitPrincipal : null,
    principalAsOf: currentFormula ? calculated.principalAsOf : null,
    principalEvidenceRef: currentFormula ? calculated.principalEvidenceRef : null,
    lastKnownEquity: currentFormula ? calculated.lastKnownEquity : null,
    lastKnownAt: currentFormula ? calculated.lastKnownAt : null,
    computedAt: currentFormula ? calculated.computedAt : null
  };
}

export function shouldRecomputeAuctionEquity(input: {
  stored?: AuctionEquityProvenance | null;
  calculated: AuctionEquityCalculation;
  estimatedEquity?: unknown;
  plv?: unknown;
  acceptedValue?: unknown;
}): boolean {
  void input.estimatedEquity;
  void input.plv;
  void input.acceptedValue;
  const stored = input.stored;
  const calculated = input.calculated;
  if (!stored) return true;
  if (!isAuctionEquityFormulaVersion(stored.formulaVersion)) return true;
  if (stored.invalidationState !== calculated.invalidationState) return true;
  if (stored.unavailableReason !== calculated.unavailableReason) return true;
  if (stored.currentAmount !== calculated.currentAmount) return true;
  if (stored.currentAmountKind !== calculated.currentAmountKind) return true;
  if (stored.currentAmountEffectiveAt !== calculated.currentAmountEffectiveAt) return true;
  if (stored.currentAmountEvidenceRef !== calculated.currentAmountEvidenceRef) return true;
  if (stored.affidavitPrincipal !== calculated.affidavitPrincipal) return true;
  if (stored.principalAsOf !== calculated.principalAsOf) return true;
  if (stored.principalEvidenceRef !== calculated.principalEvidenceRef) return true;
  if ((stored.lastKnownEquity ?? null) !== (calculated.lastKnownEquity ?? null)) return true;
  const storedCurrent = stored.invalidationState === "authoritative_invalidation" ? null : stored.lastKnownEquity;
  return storedCurrent !== (calculated.equity ?? null);
}

type NormalizedAuctionAmount = {
  kind: AuctionEquityAmountKind;
  amountCents: number | null;
  effectiveAt: string;
  evidenceRef: string;
  status: AuctionEquityFactStatus;
};

function parseAuctionAmountFact(value: unknown): AuctionAmountFact | null {
  const rec = recordFromUnknown(value);
  const kind = rec.kind === "upset_bid" || rec.kind === "reported_sale_amount" ? rec.kind : null;
  const evidenceRef = textOrNull(rec.evidenceRef ?? rec.evidence_ref);
  const effectiveAt = textOrNull(rec.effectiveAt ?? rec.effective_at);
  const status = parseFactStatus(rec.status) ?? (rec.amount == null ? "unreadable" : "valid");
  if (!kind || !evidenceRef || !effectiveAt) return null;
  return { kind, amount: rec.amount as number | string | null, effectiveAt, evidenceRef, status };
}

function parseAffidavitPrincipalFact(value: unknown): AffidavitPrincipalFact | null {
  const rec = recordFromUnknown(value);
  const evidenceRef = textOrNull(rec.evidenceRef ?? rec.evidence_ref);
  const asOf = textOrNull(rec.asOf ?? rec.as_of);
  if (!evidenceRef && rec.principal == null && !asOf) return null;
  const status = parseFactStatus(rec.status) ?? (rec.principal == null ? "missing" : "valid");
  return {
    principal: rec.principal as number | string | null,
    asOf: asOf ?? "",
    evidenceRef: evidenceRef ?? "",
    status
  };
}

function parseAuctionEquityProvenance(value: unknown): AuctionEquityProvenance | null {
  const rec = recordFromUnknown(value);
  if (!Object.keys(rec).length) return null;
  const kind = rec.currentAmountKind === "upset_bid" || rec.currentAmountKind === "reported_sale_amount"
    ? rec.currentAmountKind
    : null;
  const invalidation = AUCTION_EQUITY_INVALIDATION_STATES.includes(rec.invalidationState as AuctionEquityInvalidationState)
    ? rec.invalidationState as AuctionEquityInvalidationState
    : "current";
  const reason = AUCTION_EQUITY_UNAVAILABLE_REASONS.includes(rec.unavailableReason as AuctionEquityUnavailableReason)
    ? rec.unavailableReason as AuctionEquityUnavailableReason
    : null;
  return {
    formulaVersion: String(rec.formulaVersion ?? ""),
    currentAmount: centsToDollarsOrNull(moneyToCents(rec.currentAmount)),
    currentAmountKind: kind,
    currentAmountEffectiveAt: textOrNull(rec.currentAmountEffectiveAt),
    currentAmountEvidenceRef: textOrNull(rec.currentAmountEvidenceRef),
    affidavitPrincipal: centsToDollarsOrNull(moneyToCents(rec.affidavitPrincipal)),
    principalAsOf: textOrNull(rec.principalAsOf),
    principalEvidenceRef: textOrNull(rec.principalEvidenceRef),
    invalidationState: invalidation,
    unavailableReason: reason,
    computedAt: textOrNull(rec.computedAt),
    lastKnownEquity: centsToDollarsOrNull(moneyToCents(rec.lastKnownEquity)),
    lastKnownAt: textOrNull(rec.lastKnownAt)
  };
}

function parseFactStatus(value: unknown): AuctionEquityFactStatus | null {
  return AUCTION_EQUITY_FACT_STATUSES.includes(value as AuctionEquityFactStatus)
    ? value as AuctionEquityFactStatus
    : null;
}

function normalizeAuctionAmountFact(fact: AuctionAmountFact): NormalizedAuctionAmount | null {
  const kind = fact.kind === "upset_bid" || fact.kind === "reported_sale_amount" ? fact.kind : null;
  const evidenceRef = textOrNull(fact.evidenceRef);
  const effectiveAt = textOrNull(fact.effectiveAt);
  if (!kind || !evidenceRef || !effectiveAt) return null;
  const status = parseFactStatus(fact.status) ?? (fact.amount == null ? "unreadable" : "valid");
  return {
    kind,
    amountCents: moneyToCents(fact.amount),
    effectiveAt,
    evidenceRef,
    status
  };
}

function toAuctionAmountFact(fact: NormalizedAuctionAmount): AuctionAmountFact {
  return {
    kind: fact.kind,
    amount: fact.amountCents == null ? null : centsToDollars(fact.amountCents),
    effectiveAt: fact.effectiveAt,
    evidenceRef: fact.evidenceRef,
    status: fact.status
  };
}

function compareAmountRecency(left: NormalizedAuctionAmount, right: NormalizedAuctionAmount): number {
  if (left.effectiveAt !== right.effectiveAt) return right.effectiveAt.localeCompare(left.effectiveAt);
  return right.evidenceRef.localeCompare(left.evidenceRef);
}

function normalizeAffidavitPrincipal(fact: AffidavitPrincipalFact | null | undefined):
  | { ok: true; principal: number; principalCents: number; asOf: string; evidenceRef: string }
  | { ok: false; reason: AuctionEquityUnavailableReason } {
  if (!fact) return { ok: false, reason: "missing_affidavit_principal" };
  const status = parseFactStatus(fact.status) ?? (fact.principal == null ? "missing" : "valid");
  if (status === "ambiguous") return { ok: false, reason: "ambiguous_affidavit_principal" };
  if (status === "superseded") return { ok: false, reason: "superseded_affidavit_principal" };
  if (status === "unreadable" || status === "missing") return { ok: false, reason: "missing_affidavit_principal" };
  const asOf = textOrNull(fact.asOf);
  const evidenceRef = textOrNull(fact.evidenceRef);
  const principalCents = moneyToCents(fact.principal);
  if (!asOf || !evidenceRef || principalCents == null || status !== "valid") {
    return { ok: false, reason: "missing_affidavit_principal" };
  }
  return { ok: true, principal: centsToDollars(principalCents), principalCents, asOf, evidenceRef };
}

function mergeAuctionEquityFacts(
  explicit: AuctionEquityFacts | null | undefined,
  fromFields: AuctionEquityFacts
): AuctionEquityFacts {
  const amounts = explicit?.amounts ?? fromFields.amounts;
  const affidavitPrincipal = explicit?.affidavitPrincipal ?? fromFields.affidavitPrincipal;
  return {
    ...(amounts !== undefined ? { amounts } : {}),
    ...(affidavitPrincipal !== undefined ? { affidavitPrincipal } : {})
  };
}

function preserveLastKnown(
  stored: AuctionEquityProvenance | null,
  now: string | null,
  state: AuctionEquityInvalidationState
): AuctionEquityCalculation {
  const currentFormula = isAuctionEquityFormulaVersion(stored?.formulaVersion);
  const lastKnown = currentFormula ? stored?.lastKnownEquity ?? null : null;
  const lastKnownAt = currentFormula ? stored?.lastKnownAt ?? stored?.computedAt ?? now : now;
  return {
    available: lastKnown != null,
    equity: lastKnown,
    formulaVersion: AUCTION_EQUITY_FORMULA_VERSION,
    currentAmount: currentFormula ? stored?.currentAmount ?? null : null,
    currentAmountKind: currentFormula ? stored?.currentAmountKind ?? null : null,
    currentAmountEffectiveAt: currentFormula ? stored?.currentAmountEffectiveAt ?? null : null,
    currentAmountEvidenceRef: currentFormula ? stored?.currentAmountEvidenceRef ?? null : null,
    affidavitPrincipal: currentFormula ? stored?.affidavitPrincipal ?? null : null,
    principalAsOf: currentFormula ? stored?.principalAsOf ?? null : null,
    principalEvidenceRef: currentFormula ? stored?.principalEvidenceRef ?? null : null,
    invalidationState: state,
    unavailableReason: lastKnown == null ? "missing_current_amount" : null,
    computedAt: lastKnownAt,
    lastKnownEquity: lastKnown,
    lastKnownAt
  };
}

function clearCurrentValue(
  stored: AuctionEquityProvenance | null,
  now: string | null,
  reason: AuctionEquityUnavailableReason
): AuctionEquityCalculation {
  const currentFormula = isAuctionEquityFormulaVersion(stored?.formulaVersion);
  return {
    available: false,
    equity: null,
    formulaVersion: AUCTION_EQUITY_FORMULA_VERSION,
    currentAmount: null,
    currentAmountKind: null,
    currentAmountEffectiveAt: null,
    currentAmountEvidenceRef: null,
    affidavitPrincipal: null,
    principalAsOf: null,
    principalEvidenceRef: null,
    invalidationState: "authoritative_invalidation",
    unavailableReason: reason,
    computedAt: now,
    lastKnownEquity: currentFormula ? stored?.lastKnownEquity ?? null : null,
    lastKnownAt: currentFormula ? stored?.lastKnownAt ?? stored?.computedAt ?? now : now
  };
}

function unavailableCalculation(input: {
  stored: AuctionEquityProvenance | null;
  now: string | null;
  reason: AuctionEquityUnavailableReason;
  principal?: ReturnType<typeof normalizeAffidavitPrincipal>;
  amount?: Extract<ReturnType<typeof selectCurrentAuctionAmount>, { ok: true }>;
}): AuctionEquityCalculation {
  const principal = input.principal?.ok ? input.principal : null;
  const amount = input.amount ?? null;
  const lastKnown = isAuctionEquityFormulaVersion(input.stored?.formulaVersion) ? input.stored?.lastKnownEquity ?? null : null;
  return {
    available: false,
    equity: null,
    formulaVersion: AUCTION_EQUITY_FORMULA_VERSION,
    currentAmount: amount?.amount ?? null,
    currentAmountKind: amount?.fact.kind ?? null,
    currentAmountEffectiveAt: amount ? textOrNull(amount.fact.effectiveAt) : null,
    currentAmountEvidenceRef: amount ? textOrNull(amount.fact.evidenceRef) : null,
    affidavitPrincipal: principal?.principal ?? null,
    principalAsOf: principal?.asOf ?? null,
    principalEvidenceRef: principal?.evidenceRef ?? null,
    invalidationState: "current",
    unavailableReason: input.reason,
    computedAt: input.now,
    lastKnownEquity: lastKnown,
    lastKnownAt: input.stored?.lastKnownAt ?? input.stored?.computedAt ?? input.now
  };
}

function centsToDollarsOrNull(cents: number | null): number | null {
  return cents == null ? null : centsToDollars(cents);
}

function recordFromUnknown(value: unknown): Record<string, unknown> {
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown;
      return recordFromUnknown(parsed);
    } catch {
      return {};
    }
  }
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  return {};
}

export function closePushBlockedReason(input: {
  deceased?: unknown;
  ownerRaw?: unknown;
  propertyValue?: unknown;
  plv?: unknown;
  estimatedEquity?: unknown;
}): ClosePushBlockedReason {
  if (!isDeceased(input.deceased)) return "not_deceased";
  const owner = input.ownerRaw == null ? "" : String(input.ownerRaw).replaceAll(/\s+/g, " ").trim();
  if (!owner) return "blocked_no_owner";
  if (finiteNumber(input.propertyValue) == null) return "blocked_no_value";
  if (finiteNumber(input.plv) == null) return "blocked_no_plv";
  const equity = finiteNumber(input.estimatedEquity);
  if (equity == null) return "blocked_no_equity";
  if (equity < CLOSE_EQUITY_MIN_USD) return "blocked_equity_below_25k";
  return "blocked_no_equity";
}

function textOrNull(value: unknown): string | null {
  const text = value == null ? "" : String(value).trim();
  return text || null;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}
